import type { Feature, Polygon, MultiPolygon, Position } from "geojson";
import polygonClipping from "polygon-clipping";
import type { AnyPolygonFeature, GeometryResult } from "../types";

type PCRing = [number, number][];
type PCPolygon = PCRing[];

/** Convert GeoJSON geometry coordinates to polygon-clipping input format. Shared with
 *  `clip.ts`, which drives the same library for intersection and difference. */
export function toPolygonClippingGeom(geometry: Polygon | MultiPolygon): polygonClipping.Geom {
	if (geometry.type === "Polygon") {
		return geometry.coordinates.map((r) => r.map(([x, y]) => [x, y] as [number, number]));
	}
	return geometry.coordinates.map((p) => p.map((r) => r.map(([x, y]) => [x, y] as [number, number])));
}

export function pcPolygonToGeoJSON(p: PCPolygon): Polygon["coordinates"] {
	return p.map((r) => r.map(([x, y]) => [x, y] as Position));
}

/**
 * Merges multiple polygons/multipolygons into one, preserving holes only where no input covers.
 * Returns Polygon if single, MultiPolygon if multiple, empty MultiPolygon if no input.
 */
export function unionPolygons(shapes: AnyPolygonFeature[]): GeometryResult<AnyPolygonFeature> {
	if (shapes.length === 0) {
		return {
			ok: true,
			data: {
				type: "Feature",
				geometry: { type: "MultiPolygon", coordinates: [] },
				properties: {},
			} as Feature<MultiPolygon>,
		};
	}

	const inputs = shapes.map((s) => toPolygonClippingGeom(s.geometry));
	const [first, ...rest] = inputs;
	const unionResult = polygonClipping.union(first, ...rest);

	if (unionResult.length === 0) {
		return {
			ok: true,
			data: {
				type: "Feature",
				geometry: { type: "MultiPolygon", coordinates: [] },
				properties: {},
			} as Feature<MultiPolygon>,
		};
	}

	if (unionResult.length === 1) {
		return {
			ok: true,
			data: {
				type: "Feature",
				geometry: { type: "Polygon", coordinates: pcPolygonToGeoJSON(unionResult[0]) },
				properties: {},
			} as Feature<Polygon>,
		};
	}

	return {
		ok: true,
		data: {
			type: "Feature",
			geometry: {
				type: "MultiPolygon",
				coordinates: unionResult.map((p) => pcPolygonToGeoJSON(p)),
			},
			properties: {},
		} as Feature<MultiPolygon>,
	};
}
